import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editWitnessProction } from "services/witnessProtectionService";
import { WitnessProtection } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  protection?: WitnessProtection;
  onClose: (refetch: boolean) => void;
};

const FinalDecision: FC<Props> = ({ protection, onClose }) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<WitnessProtection>();
  useEffect(() => {
    if (protection) {
      setFormState(protection);
    }
  }, [protection]);

  const handleChange = (e: any) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (!formState?.decision_summary)
      return `${t("witness_protection.modals.final_decision.decision_summary")} ${t("required")}.`;
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const api = editWitnessProction;
    api({
      ...formState,
      id: protection?.id,
      decision_status: "Granted",
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setLoading(false);
      });
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("witness_protection.modals.final_decision.title")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("witness_protection.modals.final_decision.form_description")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-2 gap-x-4 mt-4">
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("witness_protection.modals.final_decision.decision_summary")}
              </label>
              <textarea
                rows={4}
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="decision_summary"
                value={formState?.decision_summary}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t(
                  "witness_protection.modals.final_decision.protection_agreement_time_period",
                )}
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="protection_agreement_time_period"
                value={formState?.protection_agreement_time_period}
                onChange={handleChange}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t(
                    "witness_protection.modals.final_decision.protection_begin_date",
                  )}
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="protection_agreement_time_period_begin_date"
                  value={
                    formState?.protection_agreement_time_period_begin_date
                      ? new Date(
                          formState.protection_agreement_time_period_begin_date,
                        )
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t(
                    "witness_protection.modals.final_decision.protection_end_date",
                  )}
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="protection_agreement_time_period_end_date"
                  value={
                    formState?.protection_agreement_time_period_end_date
                      ? new Date(
                          formState.protection_agreement_time_period_end_date,
                        )
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("witness_protection.modals.final_decision.date_signed")}
              </label>
              <input
                type="date"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="date_signed"
                value={
                  formState?.date_signed
                    ? new Date(formState.date_signed)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 gap-x-4 mt-4">
            <div className="flex flex-col mt-4 w-full">
              <FileUpload
                label={t(
                  "witness_protection.modals.final_decision.decision_file",
                )}
                fileTypes="*"
                info={t("upload_files")}
                required={true}
                onChange={(file) =>
                  setFormState((prev) => ({
                    ...prev,
                    decision_file: file,
                  }))
                }
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <FileUpload
                label={t(
                  "witness_protection.modals.final_decision.agreement_document",
                )}
                fileTypes="*"
                info={t("upload_files")}
                required={true}
                onChange={(file) =>
                  setFormState((prev) => ({
                    ...prev,
                    agreement_document: file,
                  }))
                }
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : (
                t("witness_protection.modals.final_decision.submit")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinalDecision;
